import {
	Directive,
	ElementRef,
	EventEmitter,
	HostListener,
	Input,
	OnInit,
	Output,
} from '@angular/core';
import { CmLocalizePipe } from '@ats/cm/core';
import { AtsNotificationService, NoticeType } from '@ats/notifications';
import { AtsFileTypes } from '@ats/utility';
import { LocalizationService } from '@cm/core/localization';
import { Papa } from 'ngx-papaparse';
import { take } from 'rxjs/operators';
import { CSVColumns } from '../models';

@Directive({
	selector: 'input[type="file"][cmCSVUpload]',
	standalone: false,
})
export class CmCSVUploadDirective implements OnInit {
	// eslint-disable-next-line
	@Input('cmCSVUpload') columns: CSVColumns[] = [];
	@Input() allowExtraColumns: boolean = false;
	@Output() parse: EventEmitter<any[]> = new EventEmitter<any>();
	@Output() err: EventEmitter<string> = new EventEmitter<string>();
	private fileReader: FileReader;

	constructor(
		private readonly notifications: AtsNotificationService,
		private readonly papa: Papa,
		private readonly el: ElementRef,
		private readonly localize: CmLocalizePipe,
		private readonly locService: LocalizationService
	) {}

	ngOnInit(): void {
		this.fileReader = new FileReader();
		this.fileReader.onload = this.handleFileRead;
		this.fileReader.onloadend = this.clearInputValue.bind(this);
	}

	@HostListener('change', ['$event.target']) handleFile(target: HTMLInputElement): void {
		if (target && target.files && target.files.length) {
			const file = target.files[0];
			if (
				(file.type === AtsFileTypes.CSV || file.type === AtsFileTypes.FIREFOX_EXCEL) &&
				file.name.endsWith('.csv')
			) {
				this.notifications.load('csv-parse');
				this.fileReader.readAsText(target.files[0]);
			} else {
				const message = this.localize.transform(
					'The file must be a CSV file.',
					'Core/System.CsvUpload.NotificationMessage.CsvFileError'
				);
				this.err.emit(message);
			}
		}
	}

	private clearInputValue(): void {
		if (this.el && this.el.nativeElement) {
			this.el.nativeElement.value = null;
		}
	}

	private handleFileRead = (event): void => {
		if (event && event.target && event.target.result) {
			const content = event.target.result.trim();
			if (content === '') {
				this.handleEmptyFile();
				return;
			}

			this.papa.parse(event.target.result, {
				header: true,
				// transformHeader: h => h.trim(), //  https://github.com/mholt/PapaParse/issues/623
				skipEmptyLines: true,
				worker: true,
				complete: this.handleCompletion,
				error: error => this.handleError(error),
			});
		}
	};

	private handleEmptyFile(): void {
		this.notifications.unload('csv-parse');
		this.locService
			.findValue$(this.locService.Keys.Errors.CMSystemErrorCsvUploadEmptyFile)
			.pipe(take(1))
			.subscribe(message => {
				this.notifications.pop({
					id: 'csv-empty-file',
					message,
					type: NoticeType.ERROR,
				});
			});
	}

	private handleCompletion = (results): void => {
		try {
			const emptyColumns: string[] = [];
			let missingHeaders: string[] = [];
			const fieldWithIncorrectOptions: string[] = [];
			// Map file headers and catch missing field errors
			const data = results.data
				.filter(row => !Object.keys(row).every(key => !row[key]))
				.map((rawRow, idx) => {
					// Trim out the leading or trailing whitespace from the headers
					const row = {};
					Object.keys(rawRow).forEach(key => {
						row[key.trim()] = rawRow[key];
					});

					// Rebuild the rows with new properties
					const newRow = {};

					// Check for missing headers
					if (idx === 0) {
						missingHeaders = this.columns
							.filter(col => col.required)
							.filter(
								col =>
									!(
										Object.keys(row)
											.map(key => key.toLowerCase())
											.includes(col.field.toLowerCase()) ||
										(!!col.property &&
											Object.keys(row)
												.map(key => key.toLowerCase())
												.includes(col.property.toLowerCase()))
									)
							)
							.map(col => col.field);
					}

					Object.keys(row).forEach(key => {
						let column = this.columns.find(
							col =>
								col.field.toLowerCase() === key.toLowerCase() ||
								(col.property && col.property.toLowerCase() === key.toLowerCase())
						);
						if (!column) {
							if (this.allowExtraColumns) {
								column = { field: key, dynamic: true };
								this.columns.push(column);
							} else {
								return;
							}
						}
						const isEmpty = row[key] === '';
						const prop = column.property ? column.property : column.field;
						newRow[prop] = column.default && isEmpty ? column.default : row[key].trim();
						newRow[prop] = column.numeric && isEmpty ? null : newRow[prop];

						if (column.numeric && !isEmpty && !column.delimiter) {
							if (
								isNaN(newRow[prop]) &&
								!fieldWithIncorrectOptions.includes(column.field)
							) {
								fieldWithIncorrectOptions.push(column.field);
							} else {
								newRow[prop] =
									!newRow[prop] && newRow[prop].toString() !== '0'
										? null
										: +newRow[prop];
							}
						}

						if (column.boolean && !isEmpty) {
							const booleanOptions = ['0', '1', 'true', 'false'];
							if (!booleanOptions.includes(newRow[prop].toString().toLowerCase())) {
								fieldWithIncorrectOptions.push(column.field);
							} else {
								newRow[prop] =
									newRow[prop].toString().toLowerCase() === 'true' ||
									newRow[prop].toString() === '1';
							}
						}

						if (column.delimiter) {
							newRow[prop] = newRow[prop].split(column.delimiter);
							if (column.numeric) {
								if (
									isNaN(newRow[prop]) &&
									!fieldWithIncorrectOptions.includes(column.field)
								) {
									fieldWithIncorrectOptions.push(column.field);
								} else {
									newRow[prop] =
										!newRow[prop] && newRow[prop].toString() !== '0'
											? null
											: +newRow[prop];
								}
							}
						}

						const isFieldRequired = column.required;

						// Check for missing rows
						if (isFieldRequired && isEmpty && !emptyColumns.includes(column.field)) {
							emptyColumns.push(column.field);
						}

						if (
							!fieldWithIncorrectOptions.includes(column.field) &&
							column?.fieldOptions &&
							!isEmpty &&
							!column.fieldOptions.find(
								fieldOption => row[key].toLowerCase() === fieldOption.toLowerCase()
							)
						) {
							fieldWithIncorrectOptions.push(column.field);
						}
					});

					// Map any defaults that weren't supplied in the CSV
					this.columns
						.filter(col => col.default && !Object.keys(row).includes(col.field))
						.forEach(column => {
							const prop = column.property ? column.property : column.field;
							newRow[prop] = column.default;
						});

					// Check for any row dependencies
					this.columns
						.filter(col => col.dependencies)
						.forEach(column => {
							// Check for missing corequired fields
							if (
								!emptyColumns.includes(column.field) &&
								column.dependencies.corequiredAny &&
								column.dependencies.corequiredAny.some(
									req => newRow[req] || newRow[req] === 0
								) &&
								!(newRow[column.property] || newRow[column.property] === 0)
							) {
								emptyColumns.push(column.field);
							}
						});

					return newRow;
				});

			// Check for uniqueness
			this.columns
				.filter(col => col.dependencies?.uniqueWith)
				.forEach(column => {
					const prop = column.property ? column.property : column.field;
					const checkArr = [];
					for (let i = 0; i < data.length; i++) {
						const val = column.dependencies.uniqueWith.reduce((acc, current) => {
							return acc + data[i][current];
						}, data[i][prop]);
						if (!checkArr.includes(val)) {
							checkArr.push(val);
						} else {
							throw new Error(
								`${this.localize.transform(
									'Data must not contain duplicate values',
									'Core/System.CsvUpload.FieldError.ViolatesUniqueness'
								)}`
							);
						}
					}
				});

			// Some required headers were missing
			if (missingHeaders.length > 0) {
				throw new Error(
					`${this.localize.transform(
						'Required headers are missing:',
						'Core/System.CsvUpload.FieldError.MissingHeaders'
					)} ${missingHeaders.join(', ')}`
				);
			}

			// Some rows had some empty required fields
			if (emptyColumns.length > 0) {
				throw new Error(
					`${this.localize.transform(
						'Required fields are missing:',
						'Core/System.CsvUpload.FieldError.MissingFields'
					)} ${emptyColumns.join(', ')}`
				);
			}

			if (fieldWithIncorrectOptions.length) {
				throw new Error(
					`${this.localize.transform(
						'Fields with incorrect options:',
						'Core/System.CsvUpload.FieldError.IncorrectOptions'
					)} ${fieldWithIncorrectOptions.join(', ')}`
				);
			}

			this.notifications.unload('csv-parse');
			this.parse.emit(data);
		} catch (err) {
			this.handleError(err);
		}
	};

	private handleError(err: ErrorEvent | any): void {
		this.notifications.unload('csv-parse');
		this.err.emit(err?.message ? (err as ErrorEvent).message : err);
	}
}
